import { useEffect, useRef, useState } from "react";
import { Button, Card, Descriptions, Divider, Input, InputNumber, Select, Space, Spin, Tag, Row, Col, Statistic } from "antd";
import { PlusOutlined, SaveOutlined, DollarOutlined, FileTextOutlined } from "@ant-design/icons";
import axiosInstance from "../../../variables/api/axiosInstance";
import { getActiveInventaireCodeId } from "../../../variables/activeInventaire";
import InventaireSelectField from "../../../components/inventaires/InventaireSelectField";
import { rightsService } from "../../../services/rightsService";
import { useLocation, useNavigate } from "react-router-dom";
import { getParametre, saveReevaluation } from "../../../hooks/useHooks";
import { PARAMETERS } from "../../../variables/constants";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { toast } from "react-toastify";

// Composant principal
const EvaluationSinistre = () => {
    const [sinistre, setSinistre] = useState("");

    const [montant, setMontant] = useState(0);
    const [loadingSinistre, setLoadingSinistre] = useState(false);
    const [garanties, setGaranties] = useState([]);
    const [evaluation, setEvaluation] = useState({});
    const [name, setName] = useState('');
    const [evaluationLoading, setEvaluationLoading] = useState(false);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [motifs, setMotifs] = useState([]);
    const [motifsLoading, setMotifsLoading] = useState(false);
    const [selectedGarantiesList, setSelectedGarantiesList] = useState([]);
    const [evaluationsGaranties, setEvaluationsGaranties] = useState([]);
    const [motifRevision, setMotifRevision] = useState();
    const [options, setOptions] = useState([]);
    const [inventaireCodeId, setInventaireCodeId] = useState(getActiveInventaireCodeId());

    const inputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const codeId = location.state?.iz;
    const recours = location.state?.recours;

    const formatGarantieCode = (value) => {
        if (!value) return '';

        // On nettoie la valeur
        value = value.trim();

        // Si la valeur a 3 caractères ou moins, on la retourne telle quelle
        if (value.length <= 3) {
            return value;
        }

        // Sinon, on prend la première lettre de chaque mot
        return value
            .split(/\s+/)          // découpe en mots
            .map(word => word[0].toUpperCase()) // prend la première lettre
            .join('');              // joint les lettres ensemble
    }


    useEffect(() => {
        if (codeId) {
            setLoadingSinistre(true)
            axiosInstance
                .get(`/api/sinistre/codeId/${codeId}`)
                .then((res) => {
                    console.log(res.data);
                    setSinistre(res.data);
                    let m = 0.0;
                    res.data.decaissements.forEach((d) => {
                        if (!d.encaissement) {
                            m += parseFloat(d.montantGlobal);
                        }
                    });
                    setMontant(m);
                    setLoadingSinistre(false)

                    axiosInstance
                        .get(`/api/contrat/preview/${res?.data?.contrat?.codeId}`)
                        .then((response) => {
                            console.log("Preview:", response.data);
                            const engine = response?.data?.engins?.find((e) => e.codeId === res.data?.engin?.codeId);
                            console.log("Engine:", engine);
                            let g = [];
                            engine && engine?.compulsoryGuaranties.forEach((ga) => {
                                !ga?.nom?.toLowerCase()?.startsWith("surprime") && g.push({
                                    value: ga.nom, label: ga.nom, garantie: ga, code: ga?.nom?.startsWith("RC") ? "RC" : formatGarantieCode(ga?.nom)
                                });
                            });

                            console.log("Garanties:", g);
                            setGaranties(g);
                        })
                        .catch((err) => {
                            console.log(err);
                        });

                    // setGaranties(res.data?.garanties || []);
                })
                .catch((err) => {
                    setLoadingSinistre(false)
                    console.log(err);
                });
        }
        const fetchMotifs = async () => {
            setMotifsLoading(true)
            const response = await getParametre(PARAMETERS.motifRevision)
            setMotifs(response.data)
            setMotifsLoading(false)
        }
        fetchMotifs()
    }, [codeId]);

    const displayNumbers = (num) => {
        return new Intl.NumberFormat('fr-FR').format(num);
    };

    useEffect(() => {
        if (motifs) {
            setOptions(motifs.map((motif) => ({
                label: motif.valeur,
                value: motif.valeur,
            })));
        }
    }, [motifs]);

    const onNameChange = (event) => {
        setName(event.target.value);
    };

    const addItem = (e) => {
        e.preventDefault();
        if (!name?.trim()) return;

        setOptions((prevOptions) => [...prevOptions, { label: name, value: name }]);
        setName("");
        inputRef.current?.focus();
    };

    const handleSelectGarantieChange = (value) => {
        setSelectedGarantiesList(value);

        let ar = [];
        value?.forEach((g) => {
            ar.push({
                garantie: g,
                montantPrincipal: 0,
                montantHonoraires: 0,
                montantTotal: 0
            });
        });
        setEvaluationsGaranties(ar);
    };

    const handleToggleEvaluation = () => {
        setIsEvaluating(true);
    };

    const handleMontantChange = (index, field, value) => {
        const newEvaluations = [...evaluationsGaranties];
        newEvaluations[index][field] = value || 0;

        // Calculer le montant total pour cette garantie
        newEvaluations[index].montantTotal =
            newEvaluations[index].montantPrincipal + newEvaluations[index].montantHonoraires;

        setEvaluationsGaranties(newEvaluations);
    };

    const calculateTotalProvision = () => {
        return evaluationsGaranties.reduce((acc, curr) => acc + (curr.montantTotal || 0), 0);
    };

    const handleSubmit = async () => {
        setEvaluationLoading(true);
        const ev = {
            ...evaluation,
            sousEvaluations: evaluationsGaranties,
            motifRevision: motifRevision,
            sinistre: sinistre,
            nouvelleProvision: calculateTotalProvision(),
            ancienneProvision: sinistre?.evaluation,
            recours: recours ? recours : null,
            // Rattachement à l'inventaire choisi (ignoré côté backend si absent/fermé/autre organisme).
            inventaireCodeId: inventaireCodeId || null,
        };

        console.log("Evaluation: ", ev);
        try {
            await saveReevaluation(ev);
            toast.success('Reévaluation effectuée avec succès');
            setEvaluationLoading(false)

            navigate(`/details-sinistre/v2`, {
                state: { codeId: codeId }
            })
        } catch (e) {
            toast.error('Echec de l\'operation')
            setEvaluationLoading(false)

        }
        // Simulation de sauvegarde
        // setTimeout(() => {
        //     setEvaluationLoading(false);
        //     alert('Réévaluation enregistrée avec succès !');
        // }, 1500);
    };

    const isMobileSinistre = Boolean(sinistre?.referenceCode);

    return (
        <div className="layout-wrapper layout-content-navbar  ">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />

                {/* <!-- Layout container --> */}
                <div className="layout-page">
                    <NavBar />

                    {/* <!-- Content wrapper --> */}
                    <div className="content-wrapper">
                        {/* <!-- Content --> */}
                        <Spin spinning={loadingSinistre}>
                            <div style={{
                                minHeight: '100vh',
                                background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)',
                                padding: '14px'
                            }}>
                                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                                    {/* En-tête avec informations principales */}
                                    <Card
                                        style={{
                                            marginBottom: '14px',
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                            borderRadius: '16px',
                                            border: 'none',
                                            overflow: 'hidden'
                                        }}
                                        styles={{ body: { padding: 0 } }}
                                    >
                                        <div style={{
                                            background: '#242166',
                                            padding: '6px 28px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexWrap: 'wrap',
                                                gap: '6px'
                                            }}>
                                                <div>
                                                    <h1 style={{
                                                        fontSize: '28px',
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        margin: '0 0 8px 0'
                                                    }}>
                                                        Réévaluation de Sinistre
                                                    </h1>
                                                    <p style={{
                                                        color: '#bfdbfe',
                                                        fontSize: '14px',
                                                        margin: 0
                                                    }}>
                                                        Sinistre N° {sinistre?.numSinistre || "-"}
                                                    </p>
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    {sinistre?.sortDossier === "I" ? (
                                                        <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>EN INSTANCE</Tag>
                                                    ) : sinistre?.sortDossier === "T" ? (
                                                        <Tag color="red" style={{ fontSize: '14px', padding: '4px 12px' }}>TERMINÉ</Tag>
                                                    ) : sinistre?.sortDossier === "O" ? (
                                                        <Tag color="yellow" style={{ fontSize: '14px', padding: '4px 12px' }}>OUVERT</Tag>
                                                    ) : (
                                                        <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>EN INSTANCE</Tag>
                                                    )}
                                                    {isMobileSinistre && <Tag color="geekblue" style={{ fontSize: '14px', padding: '4px 12px' }}>Mobile</Tag>}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ padding: '24px', background: 'white' }}>
                                            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered>
                                                <Descriptions.Item label="Numéro de Police">
                                                    <span style={{ fontWeight: '500' }}>
                                                        {sinistre?.contrat?.numeroPoliceM || sinistre?.contrat?.numeroPoliceP || "-"}
                                                    </span>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Assuré">
                                                    <span style={{ fontWeight: '500' }}>
                                                        {`${sinistre?.contrat?.souscripteur?.prenom || ''} ${sinistre?.contrat?.souscripteur?.nom || ''}`.trim() || '-'}
                                                    </span>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Immatriculation">
                                                    <span style={{ fontWeight: '500' }}>
                                                        {sinistre?.engin?.immatriculation || "-"}
                                                    </span>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Évaluation Courante">
                                                    <span style={{ color: '#2563eb', fontWeight: '600', fontSize: '15px' }}>
                                                        {displayNumbers(sinistre?.evaluation || 0)} FCFA
                                                    </span>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Sinistre à Payer">
                                                    <span style={{ color: '#ea580c', fontWeight: '600', fontSize: '15px' }}>
                                                        {displayNumbers((sinistre?.evaluation || 0) - montant)} FCFA
                                                    </span>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Montant Réglé">
                                                    <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '15px' }}>
                                                        {displayNumbers(montant)} FCFA
                                                    </span>
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </div>
                                    </Card>

                                    {/* Section de réévaluation */}
                                    <Card
                                        style={{
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                            borderRadius: '16px',
                                            border: 'none'
                                        }}
                                        title={
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px' }}>
                                                <FileTextOutlined style={{ color: '#2563eb' }} />
                                                <span style={{ fontWeight: '600' }}>Nouvelle Évaluation</span>
                                            </div>
                                        }
                                    >
                                        <Spin spinning={loadingSinistre}>
                                            {/* Statistiques de provision */}
                                            <Row gutter={16} style={{ marginBottom: '24px' }}>
                                                <Col xs={24} md={12}>
                                                    <Card style={{
                                                        background: '#eff6ff',
                                                        border: '1px solid #bfdbfe',
                                                        borderRadius: '12px'
                                                    }}>
                                                        <Statistic
                                                            title={<span style={{ color: '#374151', fontWeight: '500' }}>Ancienne Provision</span>}
                                                            value={sinistre?.evaluation || 0}
                                                            suffix="FCFA"
                                                            valueStyle={{ color: '#1890ff', fontSize: '24px', fontWeight: 'bold' }}
                                                            formatter={(value) => displayNumbers(value)}
                                                        />
                                                    </Card>
                                                </Col>
                                                <Col xs={24} md={12}>
                                                    <Card style={{
                                                        background: '#f0fdf4',
                                                        border: '1px solid #bbf7d0',
                                                        borderRadius: '12px'
                                                    }}>
                                                        <Statistic
                                                            title={<span style={{ color: '#374151', fontWeight: '500' }}>Nouvelle Provision</span>}
                                                            value={calculateTotalProvision()}
                                                            suffix="FCFA"
                                                            valueStyle={{ color: '#52c41a', fontSize: '24px', fontWeight: 'bold' }}
                                                            formatter={(value) => displayNumbers(value)}
                                                        />
                                                    </Card>
                                                </Col>
                                            </Row>

                                            {/* Sélection de garanties */}
                                            <div>
                                                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                                                    <Select
                                                        mode="multiple"
                                                        placeholder="Sélectionner les garanties à évaluer"
                                                        className="flex-1"
                                                        style={{ width: "75%", flex: 1 }}
                                                        onChange={handleSelectGarantieChange}
                                                        options={garanties?.map((gar) => ({
                                                            value: (gar?.code?.startsWith("RC") || gar?.code?.startsWith("ICDB")) ? "RC" : gar?.label,
                                                            label: (gar?.code?.startsWith("RC") || gar?.code?.startsWith("ICDB")) ? "RC" : gar?.label
                                                        }))}
                                                    />
                                                    <Button
                                                        type="primary"
                                                        style={{
                                                            width: "25%", background: '#242166',
                                                            borderColor: '#fff'
                                                        }}
                                                        onClick={handleToggleEvaluation}
                                                        disabled={!selectedGarantiesList?.length}
                                                        icon={<DollarOutlined />}
                                                    >
                                                        Évaluer
                                                    </Button>
                                                </div>

                                                {/* Tableau d'évaluation */}
                                                {isEvaluating && selectedGarantiesList?.length > 0 && (
                                                    <Card style={{
                                                        background: '#f9fafb',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px',
                                                        marginBottom: '24px'
                                                    }}>
                                                        <div style={{ overflowX: 'auto' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                                <thead>
                                                                    <tr style={{
                                                                        background: '#242166',
                                                                        color: 'white'
                                                                    }}>
                                                                        <th style={{
                                                                            padding: '16px',
                                                                            textAlign: 'left',
                                                                            fontWeight: '600',
                                                                            borderTopLeftRadius: '8px'
                                                                        }}>Garantie</th>
                                                                        <th style={{
                                                                            padding: '16px',
                                                                            textAlign: 'center',
                                                                            fontWeight: '600'
                                                                        }}>Montant Principal</th>
                                                                        <th style={{
                                                                            padding: '16px',
                                                                            textAlign: 'center',
                                                                            fontWeight: '600'
                                                                        }}>Montant Honoraires</th>
                                                                        <th style={{
                                                                            padding: '16px',
                                                                            textAlign: 'center',
                                                                            fontWeight: '600',
                                                                            borderTopRightRadius: '8px'
                                                                        }}>Total</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {evaluationsGaranties.map((item, index) => (
                                                                        <tr
                                                                            key={index}
                                                                            style={{
                                                                                borderBottom: '1px solid #e5e7eb',
                                                                                background: 'white',
                                                                                transition: 'background 0.2s'
                                                                            }}
                                                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                                                        >
                                                                            <td style={{
                                                                                padding: '16px',
                                                                                fontWeight: '500',
                                                                                color: '#111827'
                                                                            }}>
                                                                                {garanties.find(g => g.nom === item.garantie)?.label || item.garantie}
                                                                            </td>
                                                                            <td style={{ padding: '16px' }}>
                                                                                <InputNumber
                                                                                    placeholder="Montant principal"
                                                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                                                    parser={(value) => value.replace(/\s?|(,*)/g, '')}
                                                                                    onChange={(value) => handleMontantChange(index, 'montantPrincipal', value)}
                                                                                    style={{ width: '100%' }}
                                                                                    size="large"
                                                                                    min={0}
                                                                                />
                                                                            </td>
                                                                            <td style={{ padding: '16px' }}>
                                                                                <InputNumber
                                                                                    placeholder="Montant honoraires"
                                                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                                                    parser={(value) => value.replace(/\s?|(,*)/g, '')}
                                                                                    onChange={(value) => handleMontantChange(index, 'montantHonoraires', value)}
                                                                                    style={{ width: '100%' }}
                                                                                    size="large"
                                                                                    min={0}
                                                                                />
                                                                            </td>
                                                                            <td style={{ padding: '16px' }}>
                                                                                <div style={{
                                                                                    textAlign: 'center',
                                                                                    fontWeight: 'bold',
                                                                                    color: '#2563eb',
                                                                                    fontSize: '16px'
                                                                                }}>
                                                                                    {displayNumbers(item.montantTotal || 0)} FCFA
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                                <tfoot>
                                                                    <tr style={{
                                                                        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                                                        borderTop: '2px solid #86efac'
                                                                    }}>
                                                                        <td colSpan={3} style={{
                                                                            padding: '16px',
                                                                            textAlign: 'right',
                                                                            fontWeight: 'bold',
                                                                            color: '#111827',
                                                                            fontSize: '16px'
                                                                        }}>
                                                                            Total Général:
                                                                        </td>
                                                                        <td style={{
                                                                            padding: '16px',
                                                                            textAlign: 'center',
                                                                            fontWeight: 'bold',
                                                                            color: '#16a34a',
                                                                            fontSize: '18px'
                                                                        }}>
                                                                            {displayNumbers(calculateTotalProvision())} FCFA
                                                                        </td>
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    </Card>
                                                )}

                                                {/* Motif de révision */}
                                                <div style={{ marginBottom: '24px' }}>
                                                    <label style={{
                                                        display: 'block',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        color: '#374151',
                                                        marginBottom: '8px'
                                                    }}>
                                                        Motif de la Révision <span style={{ color: '#ef4444' }}>*</span>
                                                    </label>
                                                    <Spin spinning={motifsLoading}>
                                                        <Select
                                                            placeholder="Sélectionner ou ajouter un motif"
                                                            size="large"
                                                            style={{ width: '100%' }}
                                                            dropdownRender={(menu) => (
                                                                <>
                                                                    {menu}
                                                                    <Divider style={{ margin: '8px 0' }} />
                                                                    <Space style={{ padding: '8px', width: '100%' }}>
                                                                        <Input
                                                                            placeholder="Ajouter un motif"
                                                                            ref={inputRef}
                                                                            value={name}
                                                                            onChange={onNameChange}
                                                                            onPressEnter={addItem}
                                                                        />
                                                                        <Button
                                                                            type="text"
                                                                            icon={<PlusOutlined />}
                                                                            onClick={addItem}
                                                                            style={{ color: '#2563eb' }}
                                                                        >
                                                                            Ajouter
                                                                        </Button>
                                                                    </Space>
                                                                </>
                                                            )}
                                                            options={options}
                                                            onChange={(value) => setMotifRevision(value)}
                                                        />
                                                    </Spin>
                                                </div>
                                                {/* Explication détaillée du motif */}
                                                <div style={{ marginBottom: '24px' }}>
                                                    <label style={{
                                                        display: 'block',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        color: '#374151',
                                                        marginBottom: '8px'
                                                    }}>
                                                        Explication détaillée du motif
                                                    </label>
                                                    <Input.TextArea
                                                        placeholder="Veuillez fournir une explication détaillée du motif de la réévaluation..."
                                                        rows={4}
                                                        style={{
                                                            width: '100%',
                                                            fontSize: '14px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #d1d5db'
                                                        }}
                                                        onChange={(e) => setEvaluation({ ...evaluation, explicationMotif: e.target.value })}
                                                    />
                                                </div>
                                                {/* Rattachement à un inventaire de réévaluation (optionnel) — lecture HSI116 */}
                                                {rightsService("HSI116") && (
                                                    <div style={{ marginBottom: '24px' }}>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                                                            Rattacher à un inventaire (optionnel)
                                                        </label>
                                                        <InventaireSelectField value={inventaireCodeId} onChange={setInventaireCodeId} />
                                                    </div>
                                                )}
                                                {/* Bouton de soumission */}
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px' }}>
                                                    <Button
                                                        type="primary"
                                                        size="large"
                                                        loading={evaluationLoading}
                                                        onClick={handleSubmit}
                                                        disabled={!motifRevision || evaluationsGaranties.length === 0}
                                                        icon={<SaveOutlined />}
                                                        style={{
                                                            background: '#16a34a',
                                                            borderColor: '#16a34a',
                                                            padding: '0 32px',
                                                            height: '48px',
                                                            fontSize: '16px',
                                                            fontWeight: '600'
                                                        }}
                                                    >
                                                        Enregistrer la Réévaluation
                                                    </Button>
                                                </div>
                                            </div>
                                        </Spin>
                                    </Card>
                                </div>
                            </div>
                        </Spin>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EvaluationSinistre;